import {
  Datagrid,
  DateField,
  DateTimeInput,
  FunctionField,
  List,
  SelectInput,
  TextInput,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import type { FiredAlert } from '../dataProvider';
import { ALERT_TYPE_CHOICES, alertTypeLabel } from './alertsModel';

/**
 * The fired-alert trail (D-internal-alerts, Phase 9), the read-only TRAIL half. A read-only
 * history of what alerts FIRED, the events-explorer read-only-list shape: a q input, an
 * alertType facet, a From / To firedAt range, switcher region scoping, firedAt DESC, no bulk
 * actions. Row click opens the AlertShow detail (the trigger context bag). The backend WRITES
 * this trail (the mock seeds a representative history); the admin only VIEWS it, it never fires
 * or delivers. Read-gated MORE broadly than the config (the operational roles who ACT on alerts
 * see the history): support, super_admin and fraud.
 */

/** A compact preview of the trigger context bag for the list row. */
const TriggerPreview = ({ record }: { record: FiredAlert }) => {
  const text = JSON.stringify(record.trigger ?? {});
  const shown = text.length > 60 ? `${text.slice(0, 59)}…` : text;
  return (
    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
      {shown}
    </span>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const AlertListView = () => {
  const { region } = useRegion();
  const alertFilters = [
    <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
    <SelectInput
      key="alertType"
      source="alertType"
      label="Alert type"
      choices={ALERT_TYPE_CHOICES}
    />,
    <DateTimeInput key="from" source="firedAt_gte" label="From" />,
    <DateTimeInput key="to" source="firedAt_lte" label="To" />,
  ];

  return (
    <List
      resource="firedAlerts"
      title="Fired alerts"
      filters={alertFilters}
      // Scope, not activeRegion: the switcher scopes the trail (marketsForScope); a 'global'
      // alert (system_error) is visible in every scope.
      filter={{ region }}
      sort={{ field: 'firedAt', order: 'DESC' }}
      perPage={25}
      exporter={false}
    >
      {/* Read-only: no bulk actions, no mutation. Row click opens the trigger detail. */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <DateField source="firedAt" label="Fired" showTime />
        <FunctionField
          label="Alert"
          render={(r: FiredAlert) => alertTypeLabel(r.alertType)}
        />
        <FunctionField
          label="Market"
          render={(r: FiredAlert) => String(r.market).toUpperCase()}
        />
        <FunctionField
          label="Channels"
          render={(r: FiredAlert) => (r.channelsDelivered ?? []).join(', ')}
        />
        <FunctionField
          label="Trigger"
          render={(r: FiredAlert) => <TriggerPreview record={r} />}
        />
      </Datagrid>
    </List>
  );
};

/** Gated entry point registered as the firedAlerts Resource's list. */
export const AlertList = () => (
  <RequireAccess roles={['super_admin', 'fraud', 'support']}>
    <AlertListView />
  </RequireAccess>
);
